import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { T } from "../../../app/tokens.js";
import { getProjectMedia, uploadProjectMedia, updateProjectMedia, removeProjectMedia } from "../../../data/projectMediaClient.js";
import { MediaStrip } from "./MediaStrip.jsx";
import { btn, inputStyle, muted } from "../weekly/weeklyUi.js";

// Personal, client-only presentation prefs for the Updates reader. Scoped by
// user + project; never written to shared project metadata.
export const MAX_MEDIA_STARS = 3;
export function mediaPrefsKey(userKey, projectId) {
  return userKey && projectId ? `places.projectImages.v1:${userKey}:${projectId}` : null;
}
function readMediaPrefs(key, photoDefault = false) {
  const fallback = { view: photoDefault ? "tile" : "list", stars: [] };
  if (!key) return fallback;
  try {
    const raw = JSON.parse(window.localStorage.getItem(key) || "null");
    if (!raw || typeof raw !== "object") return fallback;
    return {
      view: ["tile", "list"].includes(raw.view) ? raw.view : fallback.view,
      stars: Array.isArray(raw.stars) ? [...new Set(raw.stars.filter(id => typeof id === "string" || typeof id === "number"))] : [],
    };
  } catch { return fallback; }
}
function writeMediaPrefs(key, prefs) {
  if (!key) return;
  try { window.localStorage.setItem(key, JSON.stringify(prefs)); } catch { /* presentation only */ }
}

export function ProjectMediaSection(props) {
  // Key the owner so even the first render cannot expose another project's or
  // account's local preferences while a reset effect waits to run.
  return <ProjectMediaOwner key={JSON.stringify([props.userKey ?? null, props.projectId])} {...props} />;
}

function ProjectMediaOwner({ projectId, allowManage = true, weekDate, capturedItems, twoUp = false, userKey = null }) {
  const captured = capturedItems !== undefined;
  const [state, setState] = useState({ status: "loading", items: [], capabilities: {} });
  const [retry, setRetry] = useState(0);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const [error, setError] = useState(null);
  const [edit, setEdit] = useState(null);
  const [caption, setCaption] = useState("");
  const [remove, setRemove] = useState(null);
  const [confirmation, setConfirmation] = useState("");
  const input = useRef(null);
  const generation = useRef(0);
  useEffect(() => {
    let active = true;
    const current = ++generation.current;
    setBusy(false);
    setState({ status: "loading", items: [], capabilities: {} });
    setError(null); setEdit(null); setRemove(null);
    if (captured) {
      // Archive: render exactly the captured references; never fetch live media.
      setState({ status: "ok", items: Array.isArray(capturedItems) ? capturedItems : [], capabilities: {} });
      return () => { active = false; generation.current = current + 1; };
    }
    Promise.resolve().then(() => getProjectMedia(projectId)).then(result => {
      if (active) setState({ ...result, status: "ok" });
    }).catch(err => { if (active) setState({ status: "error", items: [], capabilities: {}, error: err.message }); });
    return () => { active = false; generation.current = current + 1; };
  }, [projectId, retry, captured, capturedItems]);
  const prefKey = twoUp ? mediaPrefsKey(userKey, projectId) : null;
  const [prefs, setPrefs] = useState(() => readMediaPrefs(prefKey, twoUp));
  const [starNotice, setStarNotice] = useState(null);
  useEffect(() => { setPrefs(readMediaPrefs(prefKey, twoUp)); setStarNotice(null); }, [prefKey, twoUp]);
  const imageIds = useMemo(() => new Set(state.items.filter(item => item.type === "image").map(item => item.id)), [state.items]);
  // Stale/deleted IDs are excluded before ranking so they never hold a slot.
  const stars = useMemo(() => prefs.stars.filter(id => imageIds.has(id)).slice(0, MAX_MEDIA_STARS), [prefs.stars, imageIds]);
  const updatePrefs = useCallback(next => { setPrefs(next); writeMediaPrefs(prefKey, next); }, [prefKey]);
  const toggleStar = id => {
    if (stars.includes(id)) { setStarNotice(null); updatePrefs({ ...prefs, stars: stars.filter(x => x !== id) }); return; }
    if (stars.length >= MAX_MEDIA_STARS) { setStarNotice(`Up to ${MAX_MEDIA_STARS} images can be starred. Unstar one to pin another.`); return; }
    setStarNotice(null);
    updatePrefs({ ...prefs, stars: [...stars, id] });
  };
    const canManage = !captured && allowManage && state.status === "ok" && state.capabilities.canManage;
  const run = async action => {
    if (busyRef.current) return;
    busyRef.current = true;
    const current = generation.current;
    setBusy(true); setError(null);
    try { await action(current); }
    catch (err) { if (current === generation.current) setError(err.message); }
    finally { busyRef.current = false; if (current === generation.current) setBusy(false); }
  };
  const uploadFiles = (files, imagesOnly = false) => {
    if (!canManage || busyRef.current || !files.length) return;
    run(async current => {
      const failures = [];
      for (const file of files) {
        if (current !== generation.current) break;
        const supported = imagesOnly ? /^image\/(png|jpeg|webp)$/.test(file.type) : /^(image\/(png|jpeg|webp)|application\/pdf)$/.test(file.type);
        if (!supported || file.size > 20 * 1024 * 1024 || file.size === 0) {
          failures.push(`${file.name}: ${!supported ? "Choose PNG, JPEG or WebP images (PDF is also supported by the picker)." : "File must be nonempty and at most 20 MB."}`);
          continue;
        }
        try {
          const item = await uploadProjectMedia(projectId, file, weekDate);
          if (current === generation.current) setState(s => ({ ...s, items: [...s.items, item] }));
        } catch (err) { failures.push(`${file.name}: ${err.message}`); }
      }
      if (current === generation.current && failures.length) setError(failures.join("\n"));
    });
  };
  const upload = event => {
    const files = [...(event.target.files || [])];
    // Allow selecting the same file again after a rejected/storage-failed
    // upload; otherwise browsers suppress the next input change event.
    event.target.value = "";
    uploadFiles(files);
  };
  const actions = item => canManage && <div style={{ marginTop: 8 }}>
    <button type="button" style={btn} disabled={busy} onClick={() => { setEdit(item.id); setCaption(item.caption); setRemove(null); setError(null); }} aria-label={`Edit caption ${item.caption}`}>Caption</button>{" "}
    <button type="button" style={btn} disabled={busy} onClick={() => { setRemove(item); setConfirmation(""); setEdit(null); setError(null); }} aria-label={`Remove ${item.caption}`}>Remove</button>
  </div>;
  // Updates reader is tiles-only; image actions live in each tile's menu.
  const startCaption = item => { setEdit(item.id); setCaption(item.caption); setRemove(null); setError(null); };
  const startRemove = item => { setRemove(item); setConfirmation(""); setEdit(null); setError(null); };
  const menuActions = item => canManage ? [
    { key: "caption", label: "Caption", disabled: busy, onSelect: () => startCaption(item) },
    { key: "remove", label: "Remove", danger: true, disabled: busy, onSelect: () => startRemove(item) },
  ] : [];
  const uploadTile = twoUp && canManage ? <button type="button" data-testid="media-upload-tile" disabled={busy} onClick={() => input.current?.click()} style={{ minHeight: 68, width: "100%", marginTop: 8, border: `1.5px dashed ${T.blue}`, borderRadius: 10, background: T.blueSoft, color: T.blue, fontSize: T.fsSm, fontWeight: T.fwBold, cursor: busy ? "wait" : "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2 }}>
    <span>{busy ? "Uploading… Please wait." : "Drag & drop images here, or click to upload."}</span>
  </button> : null;
  const title = twoUp ? "Project images" : "Floor plans & photos";
  const fileDrag = event => [...(event.dataTransfer?.types || [])].includes("Files");
  return <section aria-label={title} data-testid={`project-media-${projectId}`} data-dragging={dragging || undefined} aria-busy={busy}
    onDragEnter={twoUp && canManage ? event => { if (fileDrag(event)) { event.preventDefault(); dragDepth.current++; setDragging(true); } } : undefined}
    onDragOver={twoUp && canManage ? event => { if (fileDrag(event)) { event.preventDefault(); event.dataTransfer.dropEffect = busy ? "none" : "copy"; } } : undefined}
    onDragLeave={twoUp && canManage ? event => { event.preventDefault(); if (--dragDepth.current <= 0) { dragDepth.current = 0; setDragging(false); } } : undefined}
    onDrop={twoUp && canManage ? event => { event.preventDefault(); dragDepth.current = 0; setDragging(false); uploadFiles([...(event.dataTransfer?.files || [])], true); } : undefined}
    style={{ marginTop: twoUp ? 0 : 16, outline: dragging ? `2px dashed ${T.blue}` : undefined, outlineOffset: 5, background: dragging ? T.blueSoft : undefined }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 8 }}>
      <h3 style={{ fontSize: twoUp ? T.fsBase : T.fsSm, ...(twoUp ? { fontWeight: T.fwSemibold } : {}), color: T.dark, margin: 0 }}>{title}{twoUp && state.status === "ok" && <span data-testid="media-count" style={{ marginLeft: 8, fontSize: T.fsXs, color: T.mid, fontWeight: T.fwSemibold }}>{state.items.length} {state.items.length === 1 ? "attachment" : "attachments"}</span>}</h3>
      {canManage && !twoUp && <button type="button" style={btn} disabled={busy} onClick={() => input.current?.click()}>{busy ? "Saving…" : "Add photo"}</button>}
    </div>
    {canManage && <><input ref={input} type="file" multiple aria-label="Upload project media" accept=".png,.jpg,.jpeg,.webp,.pdf" disabled={busy} onChange={upload} style={{ display: "none" }} /><p style={{ ...muted, fontSize: T.fsXs }}>PNG, JPEG, WebP or PDF · up to 20 MB per file</p></>}
    {state.status === "loading" && <p role="status" style={muted}>Loading project media…</p>}
    {state.status === "error" && <div role="alert" style={{ color: T.red }}>{state.error} <button type="button" style={btn} onClick={() => setRetry(n => n + 1)}>Retry</button></div>}
    {state.status === "ok" && <MediaStrip items={state.items} renderActions={twoUp ? item => item.type === "image" ? null : actions(item) : actions} menuActions={twoUp ? menuActions : null} twoUp={twoUp} layout={twoUp ? "tile" : null} stars={twoUp ? stars : null} onToggleStar={twoUp ? toggleStar : null} hideKind={twoUp} emptyText={captured ? "No media was captured with this archive." : "No floor plans or photos attached to this project."} />}
    {uploadTile}
    {canManage && edit && <form onSubmit={event => { event.preventDefault(); run(async current => {
      const item = await updateProjectMedia(projectId, edit, caption, weekDate);
      if (current === generation.current) { setState(s => ({ ...s, items: s.items.map(old => old.id === item.id ? item : old) })); setEdit(null); }
    }); }}>
      <label>Caption<input autoFocus style={inputStyle} aria-label="Media caption" maxLength={500} value={caption} disabled={busy} onChange={event => setCaption(event.target.value)} /></label>
      <button type="submit" style={btn} disabled={busy || !caption.trim()}>Save caption</button>{" "}
      <button type="button" style={btn} disabled={busy} onClick={() => setEdit(null)}>Cancel</button>
    </form>}
    {canManage && remove && <form onSubmit={event => { event.preventDefault(); run(async current => {
      await removeProjectMedia(projectId, remove.id, confirmation, weekDate);
      if (current === generation.current) { setState(s => ({ ...s, items: s.items.filter(item => item.id !== remove.id) })); setRemove(null); }
    }); }}>
      <p style={{ fontSize: T.fsSm }}>Remove this attachment? Type the original filename: <strong>{remove.filename}</strong></p>
      <input autoFocus aria-label="Confirm filename" style={inputStyle} value={confirmation} disabled={busy} onChange={event => setConfirmation(event.target.value)} />
      <button type="submit" style={btn} disabled={busy || confirmation !== remove.filename}>Remove attachment</button>{" "}
      <button type="button" style={btn} disabled={busy} onClick={() => setRemove(null)}>Cancel</button>
    </form>}
    {starNotice && <div role="status" data-testid="media-star-limit" style={{ ...muted, fontSize: T.fsXs, marginTop: 8 }}>{starNotice}</div>}
    {error && <div role="alert" style={{ color: T.red, fontSize: T.fsSm, marginTop: 8 }}>{error}</div>}
  </section>;
}
